/**
 * Free gift offer (cart drawer and cart page).
 * Config comes from data attributes rendered by snippets/free-gift-*.liquid.
 *
 * The gift is only free because the shop's discount code makes it so; this script adds the gift,
 * applies the code, and removes the gift when no paid items remain.
 */
(function () {
  // One-time migration from the previous customer-history/storage implementation.
  // Run before the init guard because this asset may be included more than once.
  try {
    [localStorage, sessionStorage].forEach(function (storage) {
      Object.keys(storage).forEach(function (key) {
        if (
          key.indexOf('freeGiftDiscountIneligible:') === 0 ||
          key.indexOf('freeGiftDiscountTried:') === 0
        ) {
          storage.removeItem(key);
        }
      });
    });
  } catch (e) {
    /* Storage may be unavailable; the offer must still run. */
  }

  if (window.freeGiftInitialised) return;
  window.freeGiftInitialised = true;

  var removingGift = false;
  var autoAddingGift = false;
  var handlingIneligible = false;
  var discountRejectedThisPage = false;
  var discountEnsureAttemptedThisPage = false;

  function cartUrl() {
    return (window.routes && window.routes.cart_url) || '/cart';
  }

  function giftVariantId() {
    var el = document.querySelector('[data-free-gift-variant-id]');
    return el ? String(el.dataset.freeGiftVariantId) : '';
  }

  function giftCode() {
    var el = document.querySelector('[data-free-gift-variant-id]');
    return el && el.dataset.freeGiftCode ? el.dataset.freeGiftCode : '';
  }

  var FREE_GIFT_LINE_PROPERTY = '_free_gift';

  function isFreeGiftLine(item) {
    if (String(item.variant_id) !== giftVariantId()) return false;
    var props = item.properties;
    if (!props) return false;
    if (Array.isArray(props)) {
      return props.some(function (prop) {
        var key = prop.name || prop.first || prop.key;
        var val = prop.value != null ? prop.value : prop.last;
        return key === FREE_GIFT_LINE_PROPERTY && String(val) === '1';
      });
    }
    return props[FREE_GIFT_LINE_PROPERTY] === '1' || props[FREE_GIFT_LINE_PROPERTY] === 1;
  }

  function cartQualifiesForFreeGiftDiscount(cart) {
    return cartHasValidTrigger(cart) && cart.items.some(isFreeGiftLine);
  }

  function giftConfigEl() {
    return document.querySelector('[data-free-gift-variant-id]');
  }

  /** Prevent repeated automatic attempts only on the current page load. */
  function isDiscountEligibleForCustomer() {
    return !discountRejectedThisPage;
  }

  function markDiscountIneligible(code) {
    if (!code) code = giftCode();
    discountRejectedThisPage = true;
    discountEnsureAttemptedThisPage = false;
  }

  function setGiftButtonPending(button, pending) {
    if (!button) return;
    var label = button.dataset.freeGiftLabel || 'Add Free Gift';
    var addingLabel = button.dataset.freeGiftLabelAdding || 'Adding…';
    button.disabled = pending;
    button.textContent = pending ? addingLabel : label;
    button.classList.toggle('free-gift-banner__button--pending', pending);
  }

  function setAutoApplyButtonsPending(pending) {
    document.querySelectorAll('[data-free-gift-add][data-free-gift-auto-apply="true"]').forEach(function (button) {
      setGiftButtonPending(button, pending);
    });
  }

  /** Qualifying cart but gift not added — discount did not apply. Stop showing the banner. */
  function abortFreeGiftOfferIfNotInCart(cart) {
    if (!cart || !Array.isArray(cart.items) || !cartHasValidTrigger(cart)) return;
    if (cart.items.some(isFreeGiftLine)) return;
    markDiscountIneligible(giftCode());
  }

  function giftCodeDiscountEntry(cart, code) {
    if (!cart || !code || !Array.isArray(cart.discount_codes)) return null;
    return (
      cart.discount_codes.find(function (discount) {
        return discount.code.toUpperCase() === code.toUpperCase();
      }) || null
    );
  }

  function giftLineHasCodeAllocation(item, code) {
    if (!item || !code) return false;
    var allocs = item.line_level_discount_allocations;
    if (!Array.isArray(allocs)) return false;
    var codeUpper = code.toUpperCase();
    return allocs.some(function (allocation) {
      var app = allocation.discount_application;
      var title = (app && app.title ? app.title : '').toUpperCase();
      return title === codeUpper || title.indexOf(codeUpper) !== -1;
    });
  }

  function isPromotionalGiftDiscountWorking(cart, code) {
    if (!cart || !code || !cart.items.some(isFreeGiftLine)) return false;
    var entry = giftCodeDiscountEntry(cart, code);
    if (entry && entry.applicable === false) return false;
    if (entry && entry.applicable === true) return true;
    var giftItem = cart.items.find(isFreeGiftLine);
    if (giftLineHasCodeAllocation(giftItem, code)) return true;
    return false;
  }

  function removeGiftIfDiscountNotWorking(cart) {
    if (!cart || !Array.isArray(cart.items) || !cart.items.some(isFreeGiftLine)) {
      return Promise.resolve(cart);
    }
    var code = giftCode();
    if (!code) return Promise.resolve(cart);
    if (isPromotionalGiftDiscountWorking(cart, code)) return Promise.resolve(cart);

    return getCart().then(function (freshCart) {
      if (isPromotionalGiftDiscountWorking(freshCart, code)) return freshCart;
      return handleIneligibleDiscount(freshCart);
    });
  }

  function cartHasValidTrigger(cart) {
    var el = giftConfigEl();
    if (!cart || !Array.isArray(cart.items)) return false;

    var enforced = el && el.dataset.freeGiftTriggersEnforced === 'true';
    if (!enforced) {
      return cart.items.some(function (item) {
        return !isFreeGiftLine(item);
      });
    }

    var ids = (el.dataset.freeGiftTriggerProductIds || '').split(',').filter(Boolean);
    return cart.items.some(function (item) {
      if (isFreeGiftLine(item)) return false;
      return ids.indexOf(String(item.product_id)) !== -1;
    });
  }

  function isCartPage() {
    return !!document.getElementById('main-cart-items');
  }

  function cartItemsElement() {
    return document.querySelector('cart-drawer-items') || document.querySelector('cart-items');
  }

  function toJSON(response) {
    if (!response.ok) throw new Error('Cart request failed: ' + response.status);
    return response.json();
  }

  function getCart() {
    return fetch(cartUrl() + '.js', { credentials: 'same-origin' }).then(toJSON);
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
      credentials: 'same-origin',
    }).then(toJSON);
  }

  function hasCode(cart, code) {
    return (cart.discount_codes || []).some(function (discount) {
      return discount.code.toUpperCase() === code.toUpperCase();
    });
  }

  function discountCodesString(cart, codeToEnsure) {
    var codes = (cart && cart.discount_codes ? cart.discount_codes : []).map(function (discount) {
      return discount.code;
    });
    if (
      codeToEnsure &&
      !codes.some(function (c) {
        return c.toUpperCase() === codeToEnsure.toUpperCase();
      })
    ) {
      codes.push(codeToEnsure);
    }
    return codes.join(',');
  }

  function sectionsRequestPayload() {
    var cartItems = cartItemsElement();
    if (!cartItems || typeof cartItems.getSectionsToRender !== 'function') return null;
    var sections = cartItems.getSectionsToRender().map(function (section) {
      return section.section;
    });
    if (!sections.length) return null;
    return {
      sections: sections,
      sections_url: (window.routes && window.routes.cart_url) || '/cart',
    };
  }

  function renderCartSections(parsedState) {
    var cartItems = cartItemsElement();
    if (!cartItems || !parsedState || !parsedState.sections) return;

    if (typeof parsedState.item_count === 'number') {
      cartItems.classList.toggle('is-empty', parsedState.item_count === 0);
      var cartDrawerWrapper = document.querySelector('cart-drawer');
      var cartFooter = document.getElementById('main-cart-footer');
      if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);
      if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
    }

    cartItems.getSectionsToRender().forEach(function (section) {
      var sectionHtml = parsedState.sections[section.section];
      if (!sectionHtml) return;
      var root = document.getElementById(section.id);
      if (!root) return;
      var elementToReplace = root.querySelector(section.selector) || root;
      elementToReplace.innerHTML = cartItems.getSectionInnerHTML(sectionHtml, section.selector);
    });

  }

  function applyDiscountViaUrl(code) {
    return fetch('/discount/' + encodeURIComponent(code), { credentials: 'same-origin' });
  }

  function stripGiftDiscountCode(cart, code) {
    var remaining = (cart.discount_codes || [])
      .map(function (discount) {
        return discount.code;
      })
      .filter(function (c) {
        return c.toUpperCase() !== code.toUpperCase();
      });
    return postJSON(window.routes.cart_update_url + '.js', { discount: remaining.join(',') });
  }

  function removeAllFreeGiftLines(cart) {
    if (!cart || !Array.isArray(cart.items)) return Promise.resolve(cart);

    var lines = cart.items
      .map(function (item, index) {
        return isFreeGiftLine(item) ? index + 1 : null;
      })
      .filter(function (line) {
        return line !== null;
      })
      .sort(function (a, b) {
        return b - a;
      });

    if (!lines.length) return Promise.resolve(cart);

    removingGift = true;
    var chain = Promise.resolve();
    lines.forEach(function (line) {
      chain = chain.then(function () {
        return postJSON(window.routes.cart_change_url + '.js', { line: line, quantity: 0 });
      });
    });
    return chain;
  }

  function handleIneligibleDiscount(cart) {
    var code = giftCode();
    if (!code || handlingIneligible) return Promise.resolve(cart);

    handlingIneligible = true;
    markDiscountIneligible(code);

    var workingCart = cart;
    var useSections = !isCartPage() && !!sectionsRequestPayload();

    return removeAllFreeGiftLines(workingCart)
      .then(function (updatedCart) {
        if (updatedCart && Array.isArray(updatedCart.items)) workingCart = updatedCart;
        if (hasCode(workingCart, code)) {
          var body = {
            discount: (workingCart.discount_codes || [])
              .map(function (d) {
                return d.code;
              })
              .filter(function (c) {
                return c.toUpperCase() !== code.toUpperCase();
              })
              .join(','),
          };
          if (useSections) {
            var sectionsPayload = sectionsRequestPayload();
            body.sections = sectionsPayload.sections;
            body.sections_url = sectionsPayload.sections_url;
          }
          return postJSON(window.routes.cart_update_url + '.js', body);
        }
        return workingCart;
      })
      .then(function (resultCart) {
        return refreshCartUI(resultCart);
      })
      .catch(function (error) {
        console.error(error);
        return cart;
      })
      .finally(function () {
        handlingIneligible = false;
        removingGift = false;
      });
  }

  function validateGiftDiscountApplicability(cart, code) {
    if (!code || !cart || !Array.isArray(cart.items)) return Promise.resolve(cart);
    if (!cart.items.some(isFreeGiftLine)) return Promise.resolve(cart);
    if (!isDiscountEligibleForCustomer()) return handleIneligibleDiscount(cart);

    var entry = giftCodeDiscountEntry(cart, code);
    if (entry && entry.applicable === false) return handleIneligibleDiscount(cart);

    if (entry && entry.applicable === true) return Promise.resolve(cart);

    return getCart().then(function (freshCart) {
      if (!freshCart || !freshCart.items.some(isFreeGiftLine)) return freshCart;
      var freshEntry = giftCodeDiscountEntry(freshCart, code);
      if (!freshEntry || freshEntry.applicable === false) return handleIneligibleDiscount(freshCart);
      if (!isPromotionalGiftDiscountWorking(freshCart, code)) return handleIneligibleDiscount(freshCart);
      return freshCart;
    });
  }

  // Applies discount; pass knownCart to skip an extra GET /cart.js after add.
  function applyDiscount(code, knownCart, withSections) {
    if (!code) return Promise.resolve(knownCart || null);
    if (!isDiscountEligibleForCustomer()) return Promise.resolve(knownCart || null);

    var sectionsPayload = withSections ? sectionsRequestPayload() : null;
    var body = { discount: discountCodesString(knownCart, code) };
    if (sectionsPayload) {
      body.sections = sectionsPayload.sections;
      body.sections_url = sectionsPayload.sections_url;
    }

    return postJSON(window.routes.cart_update_url + '.js', body)
      .then(function (cart) {
        return validateGiftDiscountApplicability(cart, code);
      })
      .then(function (cart) {
        if (!cartQualifiesForFreeGiftDiscount(cart) && knownCart && !hasCode(cart, code)) {
          return applyDiscountViaUrl(code).then(function () {
            return getCart().then(function (afterUrl) {
              return validateGiftDiscountApplicability(afterUrl, code);
            });
          });
        }
        return cart;
      })
      .catch(function () {
        return applyDiscountViaUrl(code)
          .then(function () {
            return getCart();
          })
          .then(function (afterUrl) {
            return validateGiftDiscountApplicability(afterUrl, code);
          })
          .catch(function () {
            return knownCart;
          });
      });
  }

  function refreshCartIconBubble() {
    return fetch(cartUrl() + '?section_id=cart-icon-bubble', { credentials: 'same-origin' })
      .then(function (response) {
        return response.text();
      })
      .then(function (html) {
        var bubble = document.getElementById('cart-icon-bubble');
        var source = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
        if (bubble && source) bubble.innerHTML = source.innerHTML;
      });
  }

  function publishCartRefresh(cartData) {
    if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    publish(PUB_SUB_EVENTS.cartUpdate, { source: 'free-gift', cartData: cartData });
  }

  // Drawer: section HTML in one shot. Cart page: full reload (footer totals).
  function refreshCartUI(cartData) {
    if (isCartPage()) {
      window.location.reload();
      return Promise.resolve();
    }

    if (cartData && cartData.sections) {
      renderCartSections(cartData);
      publishCartRefresh(cartData);
      return Promise.resolve();
    }

    return Promise.all([refreshCartIconBubble(), getCart()]).then(function (results) {
      publishCartRefresh(results[1]);
    });
  }

  function addGiftAndApplyDiscount(variantId, code, knownCart) {
    if (!isDiscountEligibleForCustomer()) {
      return Promise.resolve(knownCart || null);
    }

    var useSections = !isCartPage() && !!sectionsRequestPayload();

    return postJSON(window.routes.cart_add_url + '.js', {
      items: [
        {
          id: parseInt(variantId, 10),
          quantity: 1,
          properties: { _free_gift: '1' },
        },
      ],
    }).then(function () {
      return applyDiscount(code, knownCart, useSections);
    });
  }

  function onAddGiftClick(button) {
    var code = button.dataset.freeGiftCode;
    discountRejectedThisPage = false;
    discountEnsureAttemptedThisPage = false;
    if (!isDiscountEligibleForCustomer()) {
      return;
    }

    setGiftButtonPending(button, true);

    getCart()
      .then(function (cart) {
        return addGiftAndApplyDiscount(button.dataset.freeGiftAdd, code, cart);
      })
      .then(function () {
        return getCart();
      })
      .then(function (cart) {
        abortFreeGiftOfferIfNotInCart(cart);
        return refreshCartUI(cart);
      })
      .catch(function (error) {
        console.error(error);
        markDiscountIneligible(code);
      })
      .finally(function () {
        if (button.isConnected) setGiftButtonPending(button, false);
      });
  }

  function shouldAutoApplyGift(cart) {
    if (!isDiscountEligibleForCustomer()) return false;
    var el = giftConfigEl();
    if (!el || el.dataset.freeGiftAutoApply !== 'true') return false;
    if (el.dataset.freeGiftOfferActive !== 'true') return false;
    if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) return false;
    if (!cartHasValidTrigger(cart)) return false;
    return !cart.items.some(isFreeGiftLine);
  }

  function autoApplyGiftIfNeeded(cart) {
    if (!isDiscountEligibleForCustomer()) {
      return;
    }
    if (autoAddingGift || removingGift || handlingIneligible || !shouldAutoApplyGift(cart)) return;

    var el = giftConfigEl();
    var variantId = el.dataset.freeGiftVariantId;
    var code = el.dataset.freeGiftCode;
    if (!variantId) return;

    autoAddingGift = true;
    var cartItems = cartItemsElement();
    if (cartItems && cartItems.classList) cartItems.classList.add('free-gift-auto-applying');
    setAutoApplyButtonsPending(true);

    addGiftAndApplyDiscount(variantId, code, cart)
      .then(function () {
        return getCart();
      })
      .then(function (freshCart) {
        abortFreeGiftOfferIfNotInCart(freshCart);
        return refreshCartUI(freshCart);
      })
      .catch(function (error) {
        console.error(error);
        markDiscountIneligible(code);
      })
      .finally(function () {
        autoAddingGift = false;
        if (cartItems && cartItems.classList) cartItems.classList.remove('free-gift-auto-applying');
        setAutoApplyButtonsPending(false);
        getCart()
          .then(abortFreeGiftOfferIfNotInCart)
          .catch(function () {});
      });
  }

  function removeDuplicateFreeGiftLines(cart) {
    if (!cart || !cart.items || removingGift || autoAddingGift || handlingIneligible) return Promise.resolve(cart);

    var freeGiftLines = cart.items
      .map(function (item, index) {
        return { item: item, line: index + 1 };
      })
      .filter(function (entry) {
        return isFreeGiftLine(entry.item);
      });

    if (freeGiftLines.length <= 1) return Promise.resolve(cart);

    removingGift = true;
    var lineToRemove = freeGiftLines[freeGiftLines.length - 1].line;
    return postJSON(window.routes.cart_change_url + '.js', { line: lineToRemove, quantity: 0 })
      .then(refreshCartUI)
      .catch(function (error) {
        console.error(error);
        removingGift = false;
        return cart;
      });
  }

  function removeOrphanedGift(cart) {
    var giftId = giftVariantId();
    if (!giftId || !cart || !cart.items || handlingIneligible) return;

    var giftIndex = cart.items.findIndex(isFreeGiftLine);
    if (giftIndex === -1) {
      removingGift = false;
      return;
    }

    if (!isDiscountEligibleForCustomer()) {
      handleIneligibleDiscount(cart);
      return;
    }

    var hasNonGiftItems = cart.items.some(function (item) {
      return !isFreeGiftLine(item);
    });
    var shouldRemove = !cartHasValidTrigger(cart) || !hasNonGiftItems;
    if (!shouldRemove || removingGift) return;

    removingGift = true;
    var line = giftIndex + 1;
    var cartItems = isCartPage() ? document.querySelector('cart-items') : document.querySelector('cart-drawer-items');

    if (cartItems && typeof cartItems.updateQuantity === 'function' && cartItems.querySelector('[data-index="' + line + '"]')) {
      cartItems.updateQuantity(line, 0);
      return;
    }

    postJSON(window.routes.cart_change_url + '.js', { line: line, quantity: 0 })
      .then(refreshCartUI)
      .catch(function (error) {
        console.error(error);
        removingGift = false;
      });
  }

  function removeFreeGiftDiscountIfIneligible(cart) {
    var code = giftCode();
    if (!code || !cart) return Promise.resolve();

    if (!isDiscountEligibleForCustomer()) {
      if (cart.items.some(isFreeGiftLine)) return handleIneligibleDiscount(cart);
      return Promise.resolve();
    }

    if (cartQualifiesForFreeGiftDiscount(cart)) {
      var entry = giftCodeDiscountEntry(cart, code);
      if (entry && entry.applicable === false) return handleIneligibleDiscount(cart);
      return Promise.resolve();
    }

    if (!hasCode(cart, code)) {
      discountEnsureAttemptedThisPage = false;
      return Promise.resolve();
    }

    return stripGiftDiscountCode(cart, code)
      .then(function () {
        discountEnsureAttemptedThisPage = false;
      })
      .catch(console.error);
  }

  function ensureGiftDiscount(cart) {
    var code = giftCode();
    if (!code || !cart || !Array.isArray(cart.items)) return;

    if (!isDiscountEligibleForCustomer()) {
      if (cart.items.some(isFreeGiftLine)) handleIneligibleDiscount(cart);
      return;
    }

    if (!cartQualifiesForFreeGiftDiscount(cart)) {
      discountEnsureAttemptedThisPage = false;
      return;
    }

    if (hasCode(cart, code)) {
      var applied = giftCodeDiscountEntry(cart, code);
      if (!applied || applied.applicable === false) {
        handleIneligibleDiscount(cart);
        return;
      }
      if (!isPromotionalGiftDiscountWorking(cart, code)) {
        removeGiftIfDiscountNotWorking(cart);
      }
      return;
    }

    if (discountEnsureAttemptedThisPage) return;
    discountEnsureAttemptedThisPage = true;

    applyDiscount(code, cart, !isCartPage() && !!sectionsRequestPayload())
      .then(refreshCartUI)
      .catch(function (error) {
        discountEnsureAttemptedThisPage = false;
        console.error(error);
      });
  }

  function handleCart(cart) {
    if (!isDiscountEligibleForCustomer() && cart && cart.items && cart.items.some(isFreeGiftLine)) {
      handleIneligibleDiscount(cart);
      return;
    }

    var chain = Promise.resolve(cart);
    if (cart && cart.items && cart.items.some(isFreeGiftLine)) {
      chain = chain.then(removeGiftIfDiscountNotWorking);
    }
    chain.then(runHandleCartMaintenance).catch(console.error);
  }

  function runHandleCartMaintenance(cart) {
    removeDuplicateFreeGiftLines(cart)
      .then(function (currentCart) {
        var workingCart = currentCart || cart;
        removeOrphanedGift(workingCart);
        return removeFreeGiftDiscountIfIneligible(workingCart);
      })
      .then(function () {
        if (Array.isArray(cart.discount_codes)) {
          ensureGiftDiscount(cart);
          return;
        }
        return getCart().then(function (freshCart) {
          if (freshCart) ensureGiftDiscount(freshCart);
        });
      })
      .catch(console.error);
  }

  function bindCheckoutDiscountGuard() {
    document.addEventListener(
      'click',
      function (event) {
        var checkoutControl = event.target.closest(
          '#CartDrawer-Checkout, cart-drawer button[name="checkout"], cart-items button[name="checkout"], #checkout'
        );
        if (!checkoutControl || !giftVariantId()) return;

        event.preventDefault();
        event.stopPropagation();

        getCart()
          .then(function (cart) {
            if (!cart.items.some(isFreeGiftLine)) return cart;
            return removeGiftIfDiscountNotWorking(cart);
          })
          .then(function () {
            var form = checkoutControl.form;
            if (form && typeof form.requestSubmit === 'function') {
              form.requestSubmit(checkoutControl);
            } else if (form) {
              form.submit();
            } else {
              window.location.href = cartUrl() + '/checkout';
            }
          })
          .catch(console.error);
      },
      true
    );
  }

  function onCartUpdate(event) {
    if (!giftVariantId()) return;
    if (event && event.source === 'free-gift') return;

    var cart = event && event.cartData;

    if (cart && Array.isArray(cart.items)) {
      if (!isDiscountEligibleForCustomer()) {
        handleCart(cart);
        return;
      }
      if (shouldAutoApplyGift(cart)) {
        autoApplyGiftIfNeeded(cart);
        return;
      }
      if (Array.isArray(cart.discount_codes)) {
        handleCart(cart);
        return;
      }
    }

    getCart()
      .then(function (fullCart) {
        if (shouldAutoApplyGift(fullCart)) {
          autoApplyGiftIfNeeded(fullCart);
          return;
        }
        handleCart(fullCart);
      })
      .catch(console.error);
  }

  document.addEventListener('click', function (event) {
    var addButton = event.target.closest('[data-free-gift-add]');
    if (addButton && !addButton.disabled) {
      event.preventDefault();
      onAddGiftClick(addButton);
    }
  });

  function init() {
    bindCheckoutDiscountGuard();
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, onCartUpdate);
    }

    if (giftVariantId()) {
      getCart().then(function (cart) {
        if (shouldAutoApplyGift(cart)) {
          autoApplyGiftIfNeeded(cart);
          return;
        }
        handleCart(cart);
      }).catch(console.error);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
